import { useCallback, useEffect, useState } from 'react';
import { useSnackbar } from 'notistack';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useMediaQuery,
} from '@mui/material';

import MetaTags from '../../components/MetaTags';
import Loading from '../../components/Loading';
import FooterMenu from '../../components/Footer/FooterMenu';
import ConfirmDialog from '../../components/ConfirmDialog';
import Container from '../../components/Layout/Container';
import StyledInput from '../../components/Form/StyledInput';
import { CustomButton } from '../../components/Buttons';
import { Heading2 } from '../../components/Heading';
import { callBackbonePostApi } from '../../services/backboneApiService';

import EventBadge from './EventBadge';
import {
  ADMIN_COPY,
  EVENT_ACTIONS,
  EVENT_COLORS,
  EVENT_LIMITS,
  EVENT_SCOPES,
  EVENT_STATUSES,
  RSVP_STATUSES,
} from './constants';
import {
  buildAttendeesCsv,
  buildCsvFileName,
  buildEmptyEventForm,
  downloadTextFile,
  eventToForm,
  extractErrorMessage,
  formatCapacitySummary,
  formatEventWhen,
  getEventAvailability,
  getRsvpStatusLabel,
  isSoftFailure,
  loadStaffName,
  mergeAttendees,
  saveStaffName,
  sortAttendees,
  sortEventsByStart,
  summarizeAttendees,
  validateEventForm,
} from './helpers';

const MAX_ATTENDEE_PAGES = 50;

const FieldError = ({ children }) => (
  <Typography sx={{ color: EVENT_COLORS.danger, fontSize: 13, mt: 0.5 }} role="alert">
    {children}
  </Typography>
);

const textareaStyle = {
  background: '#3C3F40',
  fontSize: 16,
  width: '100%',
  minHeight: 120,
  border: 0,
  outline: 'none',
  padding: 16,
  boxSizing: 'border-box',
  color: '#fff',
  fontFamily: 'inherit',
  resize: 'vertical',
};

const EventFormDialog = ({ open, event, isSaving, onClose, onSubmit }) => {
  const isFullScreen = useMediaQuery('(max-width:600px)');
  const isEdit = Boolean(event);
  const [form, setForm] = useState(buildEmptyEventForm());
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (open) {
      setForm(event ? eventToForm(event) : buildEmptyEventForm());
      setErrors({});
    }
  }, [open, event]);

  const setField = (field) => (changeEvent) => {
    const { type, checked, value } = changeEvent.target;
    setForm((current) => ({ ...current, [field]: type === 'checkbox' ? checked : value }));
  };

  const handleSubmit = () => {
    const validation = validateEventForm(form, { isEdit });
    setErrors(validation.errors);
    if (validation.isValid) {
      onSubmit(validation.fields);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullScreen={isFullScreen} fullWidth maxWidth="sm">
      <DialogTitle>{isEdit ? 'Edit event' : 'New event'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Box>
            <StyledInput label="Title" value={form.title} onChange={setField('title')} />
            {errors.title && <FieldError>{errors.title}</FieldError>}
          </Box>
          <Box>
            <Typography fontWeight={700} mb={1}>
              Description
            </Typography>
            <textarea
              aria-label="Description"
              style={textareaStyle}
              value={form.description}
              onChange={setField('description')}
            />
            {errors.description && <FieldError>{errors.description}</FieldError>}
          </Box>
          <Box>
            <StyledInput label="Location" value={form.location} onChange={setField('location')} />
            {errors.location && <FieldError>{errors.location}</FieldError>}
          </Box>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <Box flex={1}>
              <StyledInput
                label="Starts"
                type="datetime-local"
                value={form.startsAt}
                onChange={setField('startsAt')}
              />
              {errors.startsAt && <FieldError>{errors.startsAt}</FieldError>}
            </Box>
            <Box flex={1}>
              <StyledInput
                label="Ends (optional)"
                type="datetime-local"
                value={form.endsAt}
                onChange={setField('endsAt')}
              />
              {errors.endsAt && <FieldError>{errors.endsAt}</FieldError>}
            </Box>
          </Stack>
          <Box>
            <StyledInput
              label="Capacity (blank for unlimited)"
              type="number"
              inputMode="numeric"
              min={1}
              value={form.capacity}
              onChange={setField('capacity')}
            />
            {errors.capacity && <FieldError>{errors.capacity}</FieldError>}
          </Box>
          <Box>
            <FormControlLabel
              control={<Checkbox checked={form.allowGuests} onChange={setField('allowGuests')} />}
              label="Allow guests (no account)"
            />
            <FormControlLabel
              control={
                <Checkbox checked={form.allowWaitlist} onChange={setField('allowWaitlist')} />
              }
              label="Allow waitlist when full"
            />
            {isEdit && (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={form.status === EVENT_STATUSES.CLOSED}
                    onChange={(changeEvent) =>
                      setForm((current) => ({
                        ...current,
                        status: changeEvent.target.checked
                          ? EVENT_STATUSES.CLOSED
                          : EVENT_STATUSES.OPEN,
                      }))
                    }
                  />
                }
                label="Close RSVPs"
              />
            )}
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={isSaving} sx={{ textTransform: 'none' }}>
          Cancel
        </Button>
        <CustomButton loading={isSaving} onClick={handleSubmit} sx={{ height: 40 }}>
          {isEdit ? 'Save changes' : 'Create event'}
        </CustomButton>
      </DialogActions>
    </Dialog>
  );
};

const AttendeesDialog = ({ event, staffName, onClose, onChanged }) => {
  const { enqueueSnackbar } = useSnackbar();
  const isFullScreen = useMediaQuery('(max-width:600px)');
  const [attendees, setAttendees] = useState([]);
  const [nextKey, setNextKey] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [removeTarget, setRemoveTarget] = useState(null);

  const fetchPage = useCallback(
    async (lastEvaluatedKey) => {
      const response = await callBackbonePostApi({
        action: EVENT_ACTIONS.GET_ATTENDEES,
        staffName,
        eventId: event.eventId,
        limit: EVENT_LIMITS.ATTENDEES_PAGE,
        ...(lastEvaluatedKey ? { lastEvaluatedKey } : {}),
      });
      if (isSoftFailure(response) || !Array.isArray(response?.data?.attendees)) {
        throw response;
      }
      return response.data;
    },
    [event, staffName]
  );

  const loadFirstPage = useCallback(async () => {
    setIsLoading(true);
    setLoadError('');
    try {
      const data = await fetchPage(null);
      setAttendees(data.attendees);
      setNextKey(data.lastEvaluatedKey || null);
    } catch (error) {
      setLoadError(extractErrorMessage(error));
    } finally {
      setIsLoading(false);
    }
  }, [fetchPage]);

  useEffect(() => {
    if (event) {
      setAttendees([]);
      setNextKey(null);
      loadFirstPage();
    }
  }, [event, loadFirstPage]);

  const loadMore = async () => {
    setIsLoading(true);
    try {
      const data = await fetchPage(nextKey);
      setAttendees((current) => mergeAttendees(current, data.attendees));
      setNextKey(data.lastEvaluatedKey || null);
    } catch (error) {
      enqueueSnackbar(extractErrorMessage(error), { variant: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  const exportCsv = async () => {
    setIsExporting(true);
    try {
      let all = attendees;
      let key = nextKey;
      let pages = 0;
      while (key && pages < MAX_ATTENDEE_PAGES) {
        const data = await fetchPage(key);
        all = mergeAttendees(all, data.attendees);
        key = data.lastEvaluatedKey || null;
        pages += 1;
      }
      setAttendees(all);
      setNextKey(key);
      downloadTextFile(buildCsvFileName(event), buildAttendeesCsv(all), 'text/csv;charset=utf-8');
    } catch (error) {
      enqueueSnackbar(extractErrorMessage(error), { variant: 'error' });
    } finally {
      setIsExporting(false);
    }
  };

  const confirmRemove = async () => {
    const target = removeTarget;
    setRemoveTarget(null);
    try {
      const response = await callBackbonePostApi({
        action: EVENT_ACTIONS.REMOVE_ATTENDEE,
        staffName,
        eventId: event.eventId,
        attendeeId: target.attendeeId,
      });
      if (isSoftFailure(response) || !response?.data?.success) {
        throw response;
      }
      enqueueSnackbar(ADMIN_COPY.ATTENDEE_REMOVED, { variant: 'success' });
      await loadFirstPage();
      onChanged?.();
    } catch (error) {
      enqueueSnackbar(extractErrorMessage(error), { variant: 'error' });
    }
  };

  const sorted = sortAttendees(attendees);
  const summary = summarizeAttendees(attendees);

  return (
    <Dialog
      open={Boolean(event)}
      onClose={onClose}
      fullScreen={isFullScreen}
      fullWidth
      maxWidth="md"
    >
      <DialogTitle sx={{ wordBreak: 'break-word' }}>{event?.title} - attendees</DialogTitle>
      <DialogContent>
        <Typography sx={{ color: EVENT_COLORS.muted, mb: 2 }} data-testid="attendee-summary">
          {`${summary.going} going, ${summary.waitlisted} waitlisted, ${summary.maybe} maybe, ${summary.notGoing} not going, ${summary.cancelled} cancelled`}
        </Typography>
        {loadError && <Alert severity="error">{loadError}</Alert>}
        {isLoading && attendees.length === 0 && <Loading />}
        {!isLoading && !loadError && attendees.length === 0 && (
          <Typography sx={{ color: EVENT_COLORS.muted }}>No RSVPs yet.</Typography>
        )}
        {sorted.length > 0 && (
          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table size="small" sx={{ minWidth: 560 }}>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Phone</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {sorted.map((attendee) => (
                  <TableRow key={attendee.attendeeId} data-testid="attendee-row">
                    <TableCell>{attendee.displayName}</TableCell>
                    <TableCell>
                      {getRsvpStatusLabel(attendee.status)}
                      {attendee.status === RSVP_STATUSES.WAITLISTED && attendee.waitlistPosition
                        ? ` #${attendee.waitlistPosition}`
                        : ''}
                    </TableCell>
                    <TableCell>{attendee.attendeeType}</TableCell>
                    <TableCell sx={{ wordBreak: 'break-all' }}>{attendee.email}</TableCell>
                    <TableCell>{attendee.phone}</TableCell>
                    <TableCell align="right">
                      {attendee.status !== RSVP_STATUSES.CANCELLED && (
                        <Button
                          size="small"
                          color="error"
                          onClick={() => setRemoveTarget(attendee)}
                          sx={{ textTransform: 'none' }}
                        >
                          Remove
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
        {nextKey && (
          <Button onClick={loadMore} disabled={isLoading} sx={{ mt: 2, textTransform: 'none' }}>
            Load more
          </Button>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          onClick={exportCsv}
          disabled={isExporting || attendees.length === 0}
          sx={{ textTransform: 'none' }}
        >
          Export CSV
        </Button>
        <Button onClick={onClose} sx={{ textTransform: 'none' }}>
          Close
        </Button>
      </DialogActions>
      <ConfirmDialog
        open={Boolean(removeTarget)}
        title={ADMIN_COPY.REMOVE_CONFIRM_TITLE}
        description={ADMIN_COPY.REMOVE_CONFIRM_BODY}
        okButton="Remove"
        cancelButton="Keep"
        handleOk={confirmRemove}
        handleClose={() => setRemoveTarget(null)}
      />
    </Dialog>
  );
};

const EventsAdmin = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [staffName, setStaffName] = useState(loadStaffName);
  const [events, setEvents] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [formState, setFormState] = useState({ open: false, event: null });
  const [isSaving, setIsSaving] = useState(false);
  const [attendeesEvent, setAttendeesEvent] = useState(null);
  const [cancelTarget, setCancelTarget] = useState(null);

  const loadEvents = useCallback(async () => {
    setLoadError('');
    try {
      const response = await callBackbonePostApi({
        action: EVENT_ACTIONS.GET_EVENTS,
        scope: EVENT_SCOPES.ALL,
        limit: EVENT_LIMITS.LIST_LIMIT,
      });
      if (isSoftFailure(response) || !Array.isArray(response?.data?.events)) {
        throw response;
      }
      setEvents(sortEventsByStart(response.data.events, 'desc'));
    } catch (error) {
      setLoadError(extractErrorMessage(error));
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadEvents();
  }, [loadEvents]);

  const handleStaffNameChange = (changeEvent) => {
    setStaffName(changeEvent.target.value);
    saveStaffName(changeEvent.target.value);
  };

  const requireStaffName = () => {
    if (!staffName.trim()) {
      enqueueSnackbar(ADMIN_COPY.STAFF_NAME_REQUIRED, { variant: 'warning' });
      return false;
    }
    return true;
  };

  const openForm = (event = null) => {
    if (requireStaffName()) {
      setFormState({ open: true, event });
    }
  };

  const openAttendees = (event) => {
    if (requireStaffName()) {
      setAttendeesEvent(event);
    }
  };

  const saveEvent = async (fields) => {
    const isEdit = Boolean(formState.event);
    setIsSaving(true);
    try {
      const response = await callBackbonePostApi({
        action: isEdit ? EVENT_ACTIONS.UPDATE_EVENT : EVENT_ACTIONS.CREATE_EVENT,
        staffName: staffName.trim(),
        ...(isEdit ? { eventId: formState.event.eventId } : {}),
        ...fields,
      });
      if (isSoftFailure(response) || !response?.data?.success) {
        throw response;
      }
      enqueueSnackbar(isEdit ? ADMIN_COPY.EVENT_UPDATED : ADMIN_COPY.EVENT_CREATED, {
        variant: 'success',
      });
      setFormState({ open: false, event: null });
      await loadEvents();
    } catch (error) {
      enqueueSnackbar(extractErrorMessage(error), { variant: 'error' });
    } finally {
      setIsSaving(false);
    }
  };

  const confirmCancelEvent = async () => {
    const target = cancelTarget;
    setCancelTarget(null);
    try {
      const response = await callBackbonePostApi({
        action: EVENT_ACTIONS.CANCEL_EVENT,
        staffName: staffName.trim(),
        eventId: target.eventId,
      });
      if (isSoftFailure(response) || !response?.data?.success) {
        throw response;
      }
      enqueueSnackbar(ADMIN_COPY.EVENT_CANCELLED, { variant: 'success' });
      await loadEvents();
    } catch (error) {
      enqueueSnackbar(extractErrorMessage(error), { variant: 'error' });
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', background: '#0A0B0C' }}>
      <MetaTags title="Black Box VR - Events admin" />
      <Container>
        <Box sx={{ pt: { xs: 4, md: 8 }, pb: 8, maxWidth: 960, mx: 'auto' }}>
          <Heading2 sx={{ mb: 3 }}>{ADMIN_COPY.TITLE}</Heading2>

          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            alignItems={{ sm: 'flex-end' }}
            mb={4}
          >
            <StyledInput
              label={ADMIN_COPY.STAFF_NAME_LABEL}
              value={staffName}
              onChange={handleStaffNameChange}
              sx={{ flex: 1 }}
            />
            <CustomButton onClick={() => openForm(null)} sx={{ width: { xs: '100%', sm: 'auto' } }}>
              New event
            </CustomButton>
          </Stack>

          {isLoading && <Loading />}
          {!isLoading && loadError && <Alert severity="error">{loadError}</Alert>}
          {!isLoading && !loadError && events.length === 0 && (
            <Typography sx={{ color: EVENT_COLORS.muted }}>No events yet.</Typography>
          )}

          <Stack spacing={2}>
            {events.map((event) => {
              const availability = getEventAvailability(event);
              const isCancelled = event.status === EVENT_STATUSES.CANCELLED;
              return (
                <Box
                  key={event.eventId}
                  data-testid="admin-event"
                  sx={{
                    p: 2,
                    background: EVENT_COLORS.panel,
                    border: `1px solid ${EVENT_COLORS.border}`,
                    borderRadius: '8px',
                  }}
                >
                  <Stack direction="row" justifyContent="space-between" spacing={1}>
                    <Typography fontWeight={700} sx={{ wordBreak: 'break-word' }}>
                      {event.title}
                    </Typography>
                    <EventBadge availability={availability} />
                  </Stack>
                  <Typography sx={{ mt: 0.5 }}>
                    {formatEventWhen(event.startsAtMs, event.endsAtMs)}
                  </Typography>
                  <Typography sx={{ color: EVENT_COLORS.muted, fontSize: 14 }}>
                    {formatCapacitySummary(event)}
                  </Typography>
                  <Stack direction="row" flexWrap="wrap" gap={1} mt={1.5}>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => openAttendees(event)}
                      sx={{ textTransform: 'none' }}
                    >
                      Attendees
                    </Button>
                    {!isCancelled && (
                      <>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => openForm(event)}
                          sx={{ textTransform: 'none' }}
                        >
                          Edit
                        </Button>
                        <Button
                          size="small"
                          color="error"
                          variant="outlined"
                          onClick={() => requireStaffName() && setCancelTarget(event)}
                          sx={{ textTransform: 'none' }}
                        >
                          Cancel event
                        </Button>
                      </>
                    )}
                  </Stack>
                </Box>
              );
            })}
          </Stack>
        </Box>
      </Container>
      <FooterMenu />

      <EventFormDialog
        open={formState.open}
        event={formState.event}
        isSaving={isSaving}
        onClose={() => setFormState({ open: false, event: null })}
        onSubmit={saveEvent}
      />
      {attendeesEvent && (
        <AttendeesDialog
          event={attendeesEvent}
          staffName={staffName.trim()}
          onClose={() => setAttendeesEvent(null)}
          onChanged={loadEvents}
        />
      )}
      <ConfirmDialog
        open={Boolean(cancelTarget)}
        title={ADMIN_COPY.CANCEL_CONFIRM_TITLE}
        description={ADMIN_COPY.CANCEL_CONFIRM_BODY}
        okButton="Cancel event"
        cancelButton="Keep event"
        handleOk={confirmCancelEvent}
        handleClose={() => setCancelTarget(null)}
      />
    </Box>
  );
};

export default EventsAdmin;
